/* eslint-disable react/prop-types */
import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Checkbox, DatePicker, Input, message } from 'antd';
import ReactTable from 'react-table';
import moment from 'moment';
import styled from 'styled-components';
import get from 'lodash/get';
import toUpper from 'lodash/toUpper';
import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import Menu from '../../components/Menu';
import CampaignLabelSelect from '../../components/CampaignLabelSelect';
import CampaignSegmentSelect from '../../components/CampaignSegmentSelect';
import SitewidePromoFields, {
  SITEWIDE_PROMO_LABEL,
  SitewidePromoSelect,
  isFixterDeployment,
} from '../../components/SitewidePromoFields';
import { getCampaignById, updateCampaign } from './actions';

const dateTimeFormat = 'YYYY-MM-DD HH:mm';

const DivStyled = styled.div`
  padding: 20px 16px;
  background: #fbfbfb;
  .ReactTable {
    background: #fff;
    .rt-td {
      display: flex;
      align-items: center;
      ul {
        margin-bottom: 0;
      }
    }
  }
  .note {
    padding-top: 20px;
  }
`;

const getListDisplayed = (list, showAll) => {
  if (showAll) {
    return (
      <ul>
        <li>All items</li>
      </ul>
    );
  }
  return (
    <ul>
      {list.map((li) => (
        <li key={li}>{li}</li>
      ))}
    </ul>
  );
};

// The dangerouslySetInnerHTML converts "<span>" into "&LT;SPAN&GT;"
const isHtml = (input) => /&LT;(“[^”]*”|'[^’]*’|[^'”>])*&GT;/i.test(input);

const containsHtmlTag = (input) => /<(“[^”]*”|'[^’]*’|[^'”>])*>/i.test(input);

export class CampaignEditPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      campaign: null,
      tableData: [],
      start: '',
      end: '',
      sitewideSlotRefresh: 0,
    };
  }

  componentDidMount() {
    const { campaignId } = this;
    return getCampaignById(campaignId).then((campaign) => {
      const {
        name,
        code,
        discountAmountPence,
        discountAmountPercentage,
        label,
        singleUse,
        validFromDate,
        validUntilDate,
        requiredItems,
        affectedItems,
        appliedToCheckout,
        appliedToEWO,
        segment,
      } = campaign;
      const data = [
        { field: 'Campaign Id', value: campaignId },
        { field: 'Banner', value: name },
        { field: 'Code', value: code },
        {
          field: 'Discount Amount',
          value: discountAmountPence ? `£${discountAmountPence / 100}` : null,
        },
        {
          field: 'Discount Amount Percentage',
          value: discountAmountPercentage ? `${discountAmountPercentage * 100}%` : null,
        },
        { field: 'Market', value: segment || null },
        { field: 'Label', value: label },
        { field: 'Single Use', value: singleUse ? 'true' : 'false' },
        { field: 'Start', value: moment(validFromDate).format(dateTimeFormat) },
        { field: 'End', value: moment(validUntilDate).format(dateTimeFormat) },
        { field: 'Affected Items', value: getListDisplayed(affectedItems, !affectedItems.length) },
        { field: 'Required Items', value: getListDisplayed(requiredItems, false) },
        {
          field: 'Apply discount from checkout',
          value: <Checkbox checked={appliedToCheckout} disabled />,
        },
        { field: 'Apply discount from Maia & EWO', value: <Checkbox checked={appliedToEWO} disabled /> },
      ];
      this.setState({ campaign, tableData: data });
    });
  }

  closePageHandler = () => {
    this.props.history.push('/');
  };

  get campaignId() {
    return this.props.match.params.campaignId;
  }

  updateCell = (cellInfo, newValue) => {
    const { tableData } = this.state;
    const { campaignId } = this;
    const { field } = cellInfo.original;
    const rowIndex = tableData.findIndex((row) => row.field === field);

    let campaignEdit;
    let bannerName;

    if (field === 'Code' && isHtml(newValue)) {
      return message.error('HTML is not allowed in this field!', 3);
    }

    switch (field) {
      case 'Banner': {
        const name = typeof newValue === 'string' ? newValue.trim() : '';
        if (!name) {
          message.error('Banner wording is required', 3);
          return false;
        }
        if (containsHtmlTag(name)) {
          message.error('HTML is not allowed in this field!', 3);
          return false;
        }
        if (name === cellInfo.original.value) return true;
        bannerName = name;
        campaignEdit = { name };
        break;
      }
      case 'Code':
        campaignEdit = { code: newValue };
        break;
      case 'Label':
        campaignEdit = { label: newValue || null };
        break;
      case 'Market':
        campaignEdit = { segment: newValue };
        break;
      case 'Start':
        campaignEdit = { validFromDate: newValue };
        break;
      case 'End':
        campaignEdit = { validUntilDate: newValue };
        break;
      default:
        return true;
    }
    return updateCampaign(campaignId, campaignEdit)
      .then(() => {
        message.info('Campaign updated', 2);
        const newTableData = [...tableData];
        const row = newTableData[rowIndex];
        if (field === 'Label') row[cellInfo.column.id] = newValue || null;
        else if (field === 'Banner') row[cellInfo.column.id] = bannerName;
        else row[cellInfo.column.id] = newValue;
        const datesChanged = field === 'Start' || field === 'End';
        const refreshSitewideSlot = datesChanged || field === 'Banner';
        this.setState((prev) => ({
          tableData: newTableData,
          campaign: field === 'Market' ? { ...prev.campaign, segment: newValue } : prev.campaign,
          sitewideSlotRefresh: refreshSitewideSlot ? prev.sitewideSlotRefresh + 1 : prev.sitewideSlotRefresh,
        }));
      })
      .catch((err) => {
        if (get(err, 'response.data.message').includes('constraint "campaign_code_unique"')) {
          return message.error('Campaign code needs to be unique', 3);
        }
        return message.error(get(err, 'response.data.message') || 'Could not update campaign', 3);
      });
  };

  disabledDate = (current) => current && current < moment().subtract(1, 'days').endOf('day');

  renderCell = (cellInfo) => {
    const { start, end } = this.state;
    const { field } = cellInfo.original;
    const defaultVal = cellInfo.original[cellInfo.column.id];

    if (field === 'Banner') {
      return (
        <Input
          size="small"
          defaultValue={defaultVal}
          onBlur={(event) => this.updateCell(cellInfo, event.target.value)}
          onPressEnter={(event) => {
            event.preventDefault();
            event.currentTarget.blur();
          }}
        />
      );
    }
    if (field === 'Market') {
      return (
        <CampaignSegmentSelect
          value={defaultVal || undefined}
          style={{ width: 220 }}
          placeholder="No market"
          onChange={(value) => this.updateCell(cellInfo, value)}
        />
      );
    }
    if (field === 'Label') {
      const labelValue = defaultVal == null || String(defaultVal).trim() === '' ? '' : defaultVal.trim();
      return (
        <CampaignLabelSelect
          value={labelValue}
          style={{ width: 200 }}
          onChange={(e) => this.updateCell(cellInfo, e === '' ? null : e)}
          showDisabledOptions
        />
      );
    }
    if (field === 'Start') {
      return (
        <DatePicker
          size="small"
          showTime={{ defaultValue: moment('00:00', 'HH:mm') }}
          disabledDate={this.disabledDate}
          format={dateTimeFormat}
          placeholder={moment(defaultVal).format(dateTimeFormat)}
          onChange={(date, dateString) => this.setState({ start: dateString })}
          onOk={() => this.updateCell(cellInfo, start)}
        />
      );
    }
    if (field === 'End') {
      return (
        <DatePicker
          size="small"
          showTime={{ defaultValue: moment('00:00', 'HH:mm') }}
          disabledDate={this.disabledDate}
          format={dateTimeFormat}
          placeholder={moment(defaultVal).format(dateTimeFormat)}
          onChange={(date, dateString) => this.setState({ end: dateString })}
          onOk={() => this.updateCell(cellInfo, end)}
        />
      );
    }
    if (field === SITEWIDE_PROMO_LABEL) return <SitewidePromoSelect />;
    if (field === 'Code') {
      return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => this.updateCell(cellInfo, toUpper(e.target.innerHTML))}
          onKeyPress={(e) => {
            e.persist();
            if (e.key === 'Enter') {
              e.preventDefault();
              e.target.blur();
            }
          }}
          dangerouslySetInnerHTML={{
            __html: defaultVal,
          }}
        />
      );
    }
    return <div>{defaultVal}</div>;
  };

  render() {
    const { campaignId } = this;
    const { campaign, tableData, sitewideSlotRefresh } = this.state;

    if (!campaign) return <Loading />;

    const columns = [
      {
        Header: 'Field',
        accessor: 'field',
      },
      {
        Header: 'Value',
        accessor: 'value',
        Cell: this.renderCell,
      },
    ];

    const rows = [...tableData];
    if (isFixterDeployment()) {
      const applyDiscountIndex = rows.findIndex((row) => row.field === 'Apply discount from Maia & EWO');
      rows.splice(
        applyDiscountIndex + 1,
        0,
        { field: SITEWIDE_PROMO_LABEL }
      );
    }
    const table = (
      <ReactTable
        data={rows}
        columns={columns}
        showPagination={false}
        defaultPageSize={Math.max(rows.length, 10)}
      />
    );

    return (
      <Container>
        <Header className="justify">
          <Title>
            Campaign {campaignId}
            <Button type="link" onClick={this.closePageHandler} className="pull-right">
              <img
                src="/assets/images/close-black.svg"
                alt="close"
                style={{ cursor: 'pointer', height: 20 }}
              />
            </Button>
          </Title>
        </Header>

        <Menu selectedItem="campaignInfo" campaignId={campaignId} history={this.props.history} />

        <DivStyled>
          {isFixterDeployment() ? (
            <SitewidePromoFields
              mode="edit"
              campaignId={campaignId}
              segment={campaign.segment}
              slotRefresh={sitewideSlotRefresh}
            >
              {table}
            </SitewidePromoFields>
          ) : (
            table
          )}

          <p className="note">
            * Only <b>market</b>, <b>code</b>, <b>label</b>, <b>banner</b>, <b>start</b> and <b>end</b> can be edited here; alternatively
            head to <b>Affected Items</b> and <b>Required Items</b> tabs to select related items.
          </p>
        </DivStyled>
      </Container>
    );
  }
}

export default withRouter(CampaignEditPage);
